import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Settings, Save, Loader2, Clock, Shield, AlertTriangle, ScrollText, Zap } from 'lucide-react';

const SCENARIO_PRESETS = {
  conservative: { internal_window_days: 60, external_enabled: false, approval_required: true, strict_documentation: true },
  balanced: { internal_window_days: 30, external_enabled: true, approval_required: true, strict_documentation: false },
  aggressive: { internal_window_days: 14, external_enabled: true, approval_required: false, strict_documentation: false },
  custom: {},
};

const MODE_ICONS = { conservative: Shield, balanced: Settings, aggressive: Zap, custom: Settings };

export const ScenarioTab = ({ settings, onModeChange, onSettingsChange, onSave, saving, t, impact }) => {
  if (!settings) return null;
  const ModeIcon = MODE_ICONS[settings.mode] || Settings;
  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.scenariosTitle')}</CardTitle>
        <CardDescription>{t('admin.scenariosDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>{t('admin.scenarioMode')}</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scenario-mode-grid">
            {Object.entries(SCENARIO_PRESETS).map(([mode]) => {
              const Icon = MODE_ICONS[mode] || Settings;
              const isActive = settings.mode === mode;
              return (
                <button key={mode} onClick={() => onModeChange(mode)} data-testid={`scenario-mode-${mode}`}
                  className={`flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left ${isActive ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20' : 'border-border hover:border-orange-300'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`h-4 w-4 ${isActive ? 'text-orange-500' : 'text-muted-foreground'}`} />
                    <span className="font-semibold text-sm">{t(`admin.mode.${mode}`)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{t(`admin.mode.${mode}Desc`)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="internal_window">{t('admin.internalWindow')}</Label>
            <Input id="internal_window" type="number" value={settings.internal_window_days || 30} data-testid="internal-window-input"
              onChange={(e) => onSettingsChange(prev => ({ ...prev, internal_window_days: parseInt(e.target.value) || 0 }))} />
            <p className="text-xs text-muted-foreground">{t('admin.internalWindowDesc')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="escalation_days">{t('admin.escalationDays')}</Label>
            <Input id="escalation_days" type="number" value={settings.escalation_days || 14} data-testid="escalation-days-input"
              onChange={(e) => onSettingsChange(prev => ({ ...prev, escalation_days: parseInt(e.target.value) || 0 }))} />
            <p className="text-xs text-muted-foreground">{t('admin.escalationDaysDesc')}</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'external_enabled', label: t('admin.externalEnabled'), desc: t('admin.externalEnabledDesc'), default: true },
            { key: 'approval_required', label: t('admin.approvalRequired'), desc: t('admin.approvalRequiredDesc'), default: true },
            { key: 'strict_documentation', label: t('admin.strictDocs'), desc: t('admin.strictDocsDesc'), default: false },
          ].map(({ key, label, desc, default: def }) => (
            <div key={key} className="flex items-center justify-between">
              <div><Label>{label}</Label><p className="text-xs text-muted-foreground">{desc}</p></div>
              <Switch checked={settings[key] ?? def} data-testid={`switch-${key}`}
                onCheckedChange={(checked) => onSettingsChange(prev => ({ ...prev, [key]: checked }))} />
            </div>
          ))}
        </div>

        <Button onClick={onSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-settings-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <Save className="h-4 w-4 mr-2" />{t('admin.saveSettings')}
        </Button>
      </CardContent>
    </Card>

    {impact && (
      <Card data-testid="scenario-impact-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" />
            {t('admin.scenarioImpact') || 'Szenario-Auswirkungen'}
          </CardTitle>
          <CardDescription>{t('admin.scenarioImpactDesc') || 'Echtzeit-Auswirkungen der aktuellen Einstellungen'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg border ${impact.impact.escalated_requests > 0 ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : 'border-border bg-muted/30'}`}>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className={`h-4 w-4 ${impact.impact.escalated_requests > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                <span className="text-xs font-medium text-muted-foreground">{t('admin.escalatedRequests') || 'Eskalierte Anfragen'}</span>
              </div>
              <p className={`text-2xl font-bold ${impact.impact.escalated_requests > 0 ? 'text-red-600' : ''}`} data-testid="escalated-count">{impact.impact.escalated_requests}</p>
            </div>
            <div className="p-4 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">{t('admin.pendingRequests') || 'Ausstehende Anfragen'}</span>
              </div>
              <p className="text-2xl font-bold" data-testid="pending-count">{impact.impact.pending_requests}</p>
            </div>
            <div className="p-4 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">{t('admin.internalWindowItems') || 'Im internen Fenster'}</span>
              </div>
              <p className="text-2xl font-bold" data-testid="internal-window-count">{impact.impact.items_in_internal_window}</p>
            </div>
            <div className={`p-4 rounded-lg border ${impact.impact.docs_missing_blocked > 0 ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20' : 'border-border bg-muted/30'}`}>
              <div className="flex items-center gap-2 mb-1">
                <ScrollText className={`h-4 w-4 ${impact.impact.docs_missing_blocked > 0 ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                <span className="text-xs font-medium text-muted-foreground">{t('admin.docsBlocked') || 'Doku-blockiert'}</span>
              </div>
              <p className={`text-2xl font-bold ${impact.impact.docs_missing_blocked > 0 ? 'text-yellow-600' : ''}`} data-testid="docs-blocked-count">{impact.impact.docs_missing_blocked}</p>
              {impact.impact.docs_missing_total > 0 && (
                <p className="text-xs text-muted-foreground mt-1">von {impact.impact.docs_missing_total} fehlend</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Badge variant={impact.impact.external_access === 'enabled' ? 'default' : 'destructive'} className="text-xs" data-testid="external-access-badge">
              {impact.impact.external_access === 'enabled' ? (t('admin.externalOpen') || 'Extern offen') : (t('admin.externalBlocked') || 'Extern gesperrt')}
            </Badge>
            <Badge variant={settings?.approval_required ? 'secondary' : 'outline'} className="text-xs" data-testid="approval-mode-badge">
              {settings?.approval_required ? (t('admin.manualApproval') || 'Manuelle Genehmigung') : (t('admin.autoApproval') || 'Auto-Genehmigung')}
            </Badge>
            {settings?.strict_documentation && (
              <Badge variant="secondary" className="text-xs" data-testid="strict-docs-badge">
                {t('admin.strictDocsActive') || 'Strenge Dokumentation aktiv'}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    )}
    </div>
  );
};
